import ResizeObserver from 'resize-observer-polyfill';
import {AttrsSchema, Component} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {ChartsContextRegistry} from 'common/charts/contexts/registry';
import {SegmentFilterController} from 'common/charts/contexts/segment-filter-controller';
import type {FormattedRow, RawCell} from 'common/report/util/retention/types';
import {currentProjectStore} from 'common/report/stores/project-details';
import {
  getFormattedRowData,
  hasRetentionData,
  mapToSortedUiRows,
  RowWithAggregate,
} from 'common/report/data/retention/parsing';
import {processQueryResponse} from 'common/report/data/retention/request-util';
import {RetentionQueryResponse} from 'common/report/data/retention/client';
import {RetentionBookmarkParams} from 'common/report/util/retention/types';
import type {LineChartAttrs, LineChartProps} from 'common/charts/visualization/line-chart';
import {DisplayAnalysisType} from 'common/charts/visualization/line-chart/enums';
import {
  DEFAULT_CHART_WIDTH,
  DEFAULT_SELECTED_SEGMENTS_LIMIT,
  MAX_SELECTED_SEGMENTS_LIMIT,
  ReportName,
} from 'common/report/constants';
import {IChartDataItem, Popover} from 'common/charts/visualization/line-chart/types';
import {XAxisUnit} from 'common/charts/visualization/line-chart/enums';

import {template} from './template';
import css from './index.styl?inline';
import {getStackedVizLineChartConfig} from './line-chart-format';

export interface LineChartRow extends FormattedRow<RawCell> {
  color?: string;
}
interface LineChartState {
  chartRows: LineChartRow[];
  chartWidth: number; // px
}

export interface RetentionCurveChartAttrs {
  'project-timezone'?: string;
  'utc-offset'?: number;
  'show-popover'?: boolean;
}

export type RetentionCurveChartProps = Pick<RetentionCurveChart, 'data' | 'bookmarkParams'>;
export class RetentionCurveChart extends Component<
  LineChartState,
  RetentionCurveChartAttrs,
  any,
  any,
  ChartsContextRegistry
> {
  public popover?: () => Popover;

  private _data: RetentionQueryResponse;

  private _bookmarkParams: RetentionBookmarkParams;

  public chartResizeObserver: ResizeObserver;

  static get attrsSchema(): AttrsSchema<RetentionCurveChartAttrs> {
    return {
      'utc-offset': `number`,
      'project-timezone': `string`,
      'show-popover': `boolean`,
    };
  }

  get config() {
    return {
      useShadowDom: true,
      css,
      contexts: [`segmentFilterController` as const],
      defaultContexts: {
        segmentFilterController: new SegmentFilterController({
          defaultNumSelections: DEFAULT_SELECTED_SEGMENTS_LIMIT[ReportName.Retention],
          selectAllLimit: MAX_SELECTED_SEGMENTS_LIMIT[ReportName.Retention],
        }),
      },
      defaultState: {
        chartRows: [],
        chartWidth: DEFAULT_CHART_WIDTH, // px
      },
      helpers: {
        hasResults: () => hasRetentionData(this.getRowAggData()),

        getChartType: () => this._bookmarkParams.chart_type,

        isFormattingTicksX: () => this.state.chartWidth > DEFAULT_CHART_WIDTH,

        getProjectTimezone: () => this.attr(`project-timezone`) || currentProjectStore.value?.timezone,

        getUtcOffset: () => this.attr(`utc-offset`) || currentProjectStore.value?.utcOffset,

        getChartData: (): IChartDataItem[] => {
          return this.state.chartRows
            .filter((row) => this.segmentFilterController.isItemSelected(0, row.raw_head))
            .map((row) => {
              const data = row.data.map(
                (cell, idx) =>
                  [idx, this._bookmarkParams.percentage_view ? cell.percent : cell.value] as [number, number],
              );

              return {
                name: row.raw_head,
                data,
                color: row.color,
                incompleteLength: getNumIncompleteCells(row),
              };

              function getNumIncompleteCells(row) {
                const incompleteIdx = row.data.findIndex((data) => data.incomplete);
                return incompleteIdx >= 0 ? row.data.length - incompleteIdx : 0;
              }
            });
        },
        getLineChartProps: (): Partial<LineChartProps> => {
          return getStackedVizLineChartConfig(this, this._bookmarkParams);
        },

        getXAxisOptions: () => {
          return {unit: XAxisUnit.Time};
        },

        getYAxisOptions: () => {
          return this._bookmarkParams.percentage_view ? {maxPadding: 0} : {};
        },

        getLineChartAttrs: (): Partial<LineChartAttrs> => ({
          'chart-width': null,
          'display-analysis-type': DisplayAnalysisType.Linear,
          'incomplete-to-date': this.helpers.isEndDateIncomplete(),
          timezone: this.helpers.getProjectTimezone(),
          'utc-offset': this.helpers.getUtcOffset(),
          'show-popover': this.attr(`show-popover`),
          'x-axis-options': this.helpers.getXAxisOptions(),
          'y-axis-options': this.helpers.getYAxisOptions(),
          'zoom-type': `x`,
        }),

        isEndDateIncomplete: () => {
          const selectedSegmentsRows = this.selectedSegmentsRows;
          if (selectedSegmentsRows.every((row) => row.data.length < 2)) {
            return false;
          }
          for (const row of selectedSegmentsRows) {
            for (const cell of row.data) {
              if (cell.incomplete) {
                return true;
              }
            }
          }
          return false;
        },

        handleHighChartClick: (series: any, index: number) => {
          const {name: segmentKey} = series;

          const row = this.state.chartRows.filter((row) => row.head === segmentKey);
          const selectedRow = row[0];
          const rowIdx = this.state.chartRows.indexOf(selectedRow);

          this.dispatchEvent(
            new CustomEvent(`lineChartClick`, {
              bubbles: true,
              detail: {
                selectedRow,
                colIdx: index,
                rowIdx,
              },
            }),
          );
        },
      },
      template,
    };
  }

  private get segmentFilterController(): SegmentFilterController {
    return this.getContext(`segmentFilterController`);
  }

  get selectedSegmentsRows() {
    return this.state.chartRows.filter((row) => this.segmentFilterController.isItemSelected(0, row.raw_head));
  }

  updateChartRows() {
    const rows = this.getRowAggData();

    if (!rows || !hasRetentionData(rows)) {
      return;
    }

    const formattedRows = mapToSortedUiRows(rows, this.helpers.getProjectTimezone(), this._bookmarkParams);

    const chartRows = formattedRows.map((row) => ({
      ...row,
      // prioritize color assigned by main app, then fall back to self-assigned color
      color: row.selection_color,
    }));
    this.update({chartRows});
  }

  set bookmarkParams(bookmarkParams) {
    this._bookmarkParams = bookmarkParams;
    this.updateChartRows();
  }

  set data(data) {
    this._data = data;
    this.updateChartRows();
  }

  getRowAggData(): RowWithAggregate[] {
    if (!this._data || !this._bookmarkParams) {
      return null;
    }
    const {metadata, data} = processQueryResponse(this._data, this._bookmarkParams.group_by);
    return getFormattedRowData(data, metadata, this._bookmarkParams.group_by, this.helpers.getProjectTimezone());
  }

  loadSegmentFilterController() {
    const segmentFilterItems = this.state.chartRows.map((row) => ({
      label: row.raw_head,
      id: row.raw_head,
      color: row.color,
    }));

    const selectedSegmentFilterItems = this._bookmarkParams.selected_segments;

    //can't call panel context if component is not connected to DOM
    if (this.isConnected) {
      this.segmentFilterController.loadSections([{items: segmentFilterItems}]);
      this.segmentFilterController.loadPersistedSelections([selectedSegmentFilterItems]);
    }
  }

  attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);
  }

  connectedCallback() {
    this.chartResizeObserver = new ResizeObserver((entries) => {
      const {width} = entries[0].contentRect;
      this.update({
        chartWidth: width,
      });
    });
    super.connectedCallback();

    //TODO add store fetch

    this.loadSegmentFilterController();
  }

  disconnectedCallback() {
    this.chartResizeObserver.disconnect();
    super.disconnectedCallback();
  }
}

export const RetentionCurveChartEl = defineAndMakeJsxFactory<{
  props: RetentionCurveChartProps;
  attrs: RetentionCurveChartAttrs;
}>(`mp-retention-curve-chart`, RetentionCurveChart);
